import type { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { computed, inject } from '@angular/core';
import type { BaseApiClient, CrudEntitiesState } from '@indutrust/shared-model';
import { mapResponse } from '@ngrx/operators';
import {
  signalStoreFeature,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import type { EntityId } from '@ngrx/signals/entities';
import {
  addEntity,
  removeEntity,
  updateEntity,
  withEntities,
} from '@ngrx/signals/entities';
import {
  Events,
  injectDispatch,
  on,
  withEventHandlers,
  withReducer,
} from '@ngrx/signals/events';
import { switchMap } from 'rxjs';
import { resolveHttpError } from '../../../http/resolve-http-error.util';
import { withSelectedEntity } from '../../selected-entity';
import {
  setCrudError,
  setCrudLoading,
  setCrudSuccess,
} from '../crud-store.util';
import type { createCrudEvents } from './crud-entities.events';

/**
 * Helper function to build entity map from array
 */
function buildEntityMap<Entity extends { id: EntityId }>(
  entities: Entity[]
): Record<EntityId, Entity> {
  return entities.reduce<Record<EntityId, Entity>>((map, entity) => {
    map[entity.id] = entity;
    return map;
  }, {});
}

/**
 * Add event-driven CRUD state and handlers for managing an entity collection in a Signal Store.
 *
 * This is the event-driven version that uses:
 * - `withReducer` to handle CRUD events and update state
 * - `withEventHandlers` to handle API calls (if service is provided)
 * - Methods to dispatch events
 *
 * Provides request status fields and convenience selectors (loading/success/error) for
 * loadAll, loadById, add, edit and delete operations. Also composes the
 * {@link withSelectedEntity} feature.
 *
 * @template Entity The entity type which must have an `id` of type {@link EntityId}.
 * @template Service The service type that extends BaseApiClient<Entity>.
 * @param crudEvents The event group created by `createCrudEvents<Entity>()`.
 * @param serviceFactory Optional factory function that injects the service. If provided,
 *                       API calls will be automatically handled. Pass a function like
 *                       `() => inject(YourService)` or `() => inject(YourService, { optional: true })`.
 * @returns A Signal Store feature that can be composed with other features.
 * @example
 * ```typescript
 * // With automatic API handling
 * const countryEvents = createCrudEvents<Country>('country');
 *
 * export const CountryStore = signalStore(
 *   { providedIn: 'root' },
 *   withCrudEntitiesEvents(countryEvents, () => inject(CountryApiClient)),
 * );
 *
 * // Without API handling (handle in withEventHandlers yourself)
 * export const CountryStore = signalStore(
 *   { providedIn: 'root' },
 *   withCrudEntitiesEvents(countryEvents),
 *   withEventHandlers((store, events, service) => ({
 *     // custom handlers
 *   }))
 * );
 * ```
 */
export function withCrudEntitiesEvents<Entity extends { id: EntityId }>(
  crudEvents: ReturnType<typeof createCrudEvents<Entity>>,
  serviceFactory?: () => BaseApiClient<Entity>
) {
  const baseFeatures = signalStoreFeature(
    withEntities<Entity>(),
    withState<CrudEntitiesState>({
      loadAllStatus: 'initial',
      loadByIdStatus: 'initial',
      addStatus: 'initial',
      editStatus: 'initial',
      deleteStatus: 'initial',
    }),
    withSelectedEntity(),
    withComputed(
      ({
        addStatus,
        editStatus,
        deleteStatus,
        loadAllStatus,
        loadByIdStatus,
      }) => ({
        loadAllLoading: computed(() => loadAllStatus() === 'loading'),
        loadAllSuccess: computed(() => loadAllStatus() === 'success'),
        loadAllError: computed(() => {
          const status = loadAllStatus();
          return typeof status === 'object' ? status.error : null;
        }),
        loadByIdLoading: computed(() => loadByIdStatus() === 'loading'),
        loadByIdSuccess: computed(() => loadByIdStatus() === 'success'),
        loadByIdError: computed(() => {
          const status = loadByIdStatus();
          return typeof status === 'object' ? status.error : null;
        }),
        addLoading: computed(() => addStatus() === 'loading'),
        addSuccess: computed(() => addStatus() === 'success'),
        addError: computed(() => {
          const status = addStatus();
          return typeof status === 'object' ? status.error : null;
        }),
        editLoading: computed(() => editStatus() === 'loading'),
        editSuccess: computed(() => editStatus() === 'success'),
        editError: computed(() => {
          const status = editStatus();
          return typeof status === 'object' ? status.error : null;
        }),
        deleteLoading: computed(() => deleteStatus() === 'loading'),
        deleteSuccess: computed(() => deleteStatus() === 'success'),
        deleteError: computed(() => {
          const status = deleteStatus();
          return typeof status === 'object' ? status.error : null;
        }),
      })
    ),
    withComputed(
      ({
        loadAllLoading,
        loadByIdLoading,
        addLoading,
        editLoading,
        deleteLoading,
      }) => ({
        anyLoading: computed(
          (): boolean =>
            loadAllLoading() ||
            loadByIdLoading() ||
            addLoading() ||
            editLoading() ||
            deleteLoading()
        ),
      })
    ),
    // Reducer to handle all CRUD events
    withReducer(
      // LoadAll events
      on(crudEvents.loadAllRequested, () => setCrudLoading('loadAll')),
      on(crudEvents.loadAllSucceeded, ({ payload: entities }) => ({
        ...setCrudSuccess('loadAll'),
        entityMap: buildEntityMap(entities),
        ids: entities.map((e) => (e as Entity & { id: EntityId }).id),
      })),
      on(crudEvents.loadAllFailed, ({ payload: errorMessage }) =>
        setCrudError('loadAll', errorMessage)
      ),

      // LoadById events
      on(crudEvents.loadByIdRequested, () => setCrudLoading('loadById')),
      on(crudEvents.loadByIdSucceeded, ({ payload: entity }, store) => {
        const typedEntity = entity as Entity & { id: EntityId };
        return {
          ...setCrudSuccess('loadById'),
          ...updateEntity<Entity>({
            id: typedEntity.id,
            changes: entity,
          })(store),
          selectedEntityId: typedEntity.id,
        };
      }),
      on(crudEvents.loadByIdFailed, ({ payload: errorMessage }) =>
        setCrudError('loadById', errorMessage)
      ),

      // Add events
      on(crudEvents.addRequested, () => setCrudLoading('add')),
      on(crudEvents.addSucceeded, ({ payload: entity }, store) => ({
        ...setCrudSuccess('add'),
        ...addEntity(entity)(store),
      })),
      on(crudEvents.addFailed, ({ payload: errorMessage }) =>
        setCrudError('add', errorMessage)
      ),

      // Edit events
      on(crudEvents.editRequested, () => setCrudLoading('edit')),
      on(crudEvents.editSucceeded, ({ payload: entity }, store) => {
        const typedEntity = entity as Entity & { id: EntityId };
        return {
          ...setCrudSuccess('edit'),
          ...updateEntity<Entity>({
            id: typedEntity.id,
            changes: entity,
          })(store),
        };
      }),
      on(crudEvents.editFailed, ({ payload: errorMessage }) =>
        setCrudError('edit', errorMessage)
      ),

      // Delete events
      on(crudEvents.deleteRequested, () => setCrudLoading('delete')),
      on(crudEvents.deleteSucceeded, ({ payload: id }, store) => ({
        ...setCrudSuccess('delete'),
        ...removeEntity(id)(store),
      })),
      on(crudEvents.deleteFailed, ({ payload: errorMessage }) =>
        setCrudError('delete', errorMessage)
      ),

      on(crudEvents.selectedEntityChanged, ({ payload }) => ({
        selectedEntityId: payload,
      }))
    ),
    withMethods(() => {
      const dispatch = injectDispatch(crudEvents);
      return {
        /**
         * Dispatch loadAll event
         */
        loadAll(
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.loadAllRequested({ urlSegments, params });
        },
        /**
         * Dispatch loadById event
         */
        loadById(
          id: EntityId,
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.loadByIdRequested({ id, urlSegments, params });
        },
        /**
         * Dispatch add event
         */
        add(
          payload: Partial<Entity>,
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.addRequested({ payload, urlSegments, params });
        },
        /**
         * Dispatch edit event
         */
        edit(
          id: EntityId,
          payload: Partial<Entity>,
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.editRequested({ id, payload, urlSegments, params });
        },
        /**
         * Dispatch delete event
         */
        delete(
          id: EntityId,
          urlSegments?: Record<string, string | number>,
          params?: HttpParams
        ): void {
          dispatch.deleteRequested({ id, urlSegments, params });
        },
        /**
         * Dispatch selectedEntityChanged event
         */
        setSelectedEntity(selectedEntityId: EntityId | null): void {
          dispatch.selectedEntityChanged(selectedEntityId);
        },
      };
    })
  );

  // If service factory is provided, add event handlers
  if (serviceFactory) {
    return signalStoreFeature(
      baseFeatures,
      withEventHandlers(
        (store, events = inject(Events), service = serviceFactory()) => {
          return {
            /**
             * Handles entity loadAll requests.
             * Fetches entities from the API and dispatches success/failure events.
             */
            loadAllEntities$: events.on(crudEvents.loadAllRequested).pipe(
              switchMap(({ payload }) =>
                service.getAll(payload.urlSegments, payload.params).pipe(
                  mapResponse({
                    next: (entities) => crudEvents.loadAllSucceeded(entities),
                    error: (error: HttpErrorResponse) =>
                      crudEvents.loadAllFailed(resolveHttpError(error).code),
                  })
                )
              )
            ),
            /**
             * Handles entity loadById requests.
             * Fetches a single entity from the API and dispatches success/failure events.
             */
            loadByIdEntity$: events.on(crudEvents.loadByIdRequested).pipe(
              switchMap(({ payload }) =>
                service.getById(payload.id, payload.urlSegments).pipe(
                  mapResponse({
                    next: (entity) => crudEvents.loadByIdSucceeded(entity),
                    error: (error: HttpErrorResponse) =>
                      crudEvents.loadByIdFailed(resolveHttpError(error).code),
                  })
                )
              )
            ),
            /**
             * Handles entity add requests.
             * Creates a new entity via the API and dispatches success/failure events.
             */
            addEntity$: events.on(crudEvents.addRequested).pipe(
              switchMap(({ payload }) =>
                service.add(payload.payload, payload.urlSegments).pipe(
                  mapResponse({
                    next: (entity) => crudEvents.addSucceeded(entity),
                    error: (error: HttpErrorResponse) =>
                      crudEvents.addFailed(resolveHttpError(error).code),
                  })
                )
              )
            ),
            /**
             * Handles entity edit requests.
             * Updates an entity via the API and dispatches success/failure events.
             */
            editEntity$: events.on(crudEvents.editRequested).pipe(
              switchMap(({ payload }) =>
                service
                  .patch(payload.id, payload.payload, payload.urlSegments)
                  .pipe(
                    mapResponse({
                      next: (entity) => crudEvents.editSucceeded(entity),
                      error: (error: HttpErrorResponse) =>
                        crudEvents.editFailed(resolveHttpError(error).code),
                    })
                  )
              )
            ),
            /**
             * Handles entity delete requests.
             * Deletes an entity via the API and dispatches success/failure events.
             */
            deleteEntity$: events.on(crudEvents.deleteRequested).pipe(
              switchMap(({ payload }) =>
                service.delete(payload.id, payload.urlSegments).pipe(
                  mapResponse({
                    next: () => crudEvents.deleteSucceeded(payload.id),
                    error: (error: HttpErrorResponse) =>
                      crudEvents.deleteFailed(resolveHttpError(error).code),
                  })
                )
              )
            ),
          };
        }
      )
    );
  }

  return baseFeatures;
}
